import type { PropertyDescriptor } from '../core/definitions';
import type { SettingsBag } from '../core/model';
import { bool, num, str } from './base';
import { breakPieces, graphemes, transformText, type TextTransform } from './textSegment';

export interface TextStyle {
  fontFamily: string;
  fontSize: number;
  fontWeight: number;
  italic: boolean;
  color: string;
  backgroundColor: string;
  outlineColor: string;
  outlineWidth: number;
  align: 'left' | 'center' | 'right';
  /** Vertical placement inside a fixed `boxHeight`. */
  valign: 'top' | 'middle' | 'bottom';
  /** Multiple of fontSize. */
  lineHeight: number;
  /** Wrap width in px; 0 disables wrapping (unless `boxWidth` is set). */
  wrapWidth: number;
  /** Fixed box size in px; 0 fits the text. Text outside a fixed box is clipped. */
  boxWidth: number;
  boxHeight: number;
  padding: number;
  /** Extra space between characters in px (Chromium 99+, Safari 17+; ignored elsewhere). */
  letterSpacing: number;
  textTransform: TextTransform;
  /** Every digit takes the width of the widest one, so changing numbers do not move the text. */
  tabularDigits: boolean;
  shadow: boolean;
  shadowColor: string;
  shadowBlur: number;
  shadowOffsetX: number;
  shadowOffsetY: number;
}

export const TEXT_STYLE_DEFAULTS: TextStyle = {
  fontFamily: 'sans-serif',
  fontSize: 64,
  fontWeight: 400,
  italic: false,
  color: '#ffffff',
  backgroundColor: '#00000000',
  outlineColor: '#000000',
  outlineWidth: 0,
  align: 'left',
  valign: 'top',
  lineHeight: 1.2,
  wrapWidth: 0,
  boxWidth: 0,
  boxHeight: 0,
  padding: 0,
  letterSpacing: 0,
  textTransform: 'none',
  tabularDigits: false,
  shadow: false,
  shadowColor: '#000000cc',
  shadowBlur: 4,
  shadowOffsetX: 2,
  shadowOffsetY: 2,
};

/** Largest canvas edge a text or shape source rasterises to: the compositor's texture limit. */
export const MAX_RASTER_EDGE = 8192;

export function readTextStyle(s: SettingsBag): TextStyle {
  const d = TEXT_STYLE_DEFAULTS;
  const align = str(s.align, d.align);
  const valign = str(s.valign, d.valign);
  const transform = str(s.textTransform, d.textTransform);
  return {
    fontFamily: str(s.fontFamily, d.fontFamily).trim() || d.fontFamily,
    fontSize: num(s.fontSize, d.fontSize, 1, 1000),
    fontWeight: Math.round(num(s.fontWeight, d.fontWeight, 100, 900)),
    italic: bool(s.italic, d.italic),
    color: str(s.color, d.color),
    backgroundColor: str(s.backgroundColor, d.backgroundColor),
    outlineColor: str(s.outlineColor, d.outlineColor),
    outlineWidth: num(s.outlineWidth, d.outlineWidth, 0, 100),
    align: align === 'center' || align === 'right' ? align : 'left',
    valign: valign === 'middle' || valign === 'bottom' ? valign : 'top',
    lineHeight: num(s.lineHeight, d.lineHeight, 0.5, 5),
    wrapWidth: num(s.wrapWidth, d.wrapWidth, 0, 16384),
    boxWidth: num(s.boxWidth, d.boxWidth, 0, 16384),
    boxHeight: num(s.boxHeight, d.boxHeight, 0, 16384),
    padding: num(s.padding, d.padding, 0, 1000),
    letterSpacing: num(s.letterSpacing, d.letterSpacing, -100, 1000),
    textTransform: transform === 'uppercase' || transform === 'lowercase' || transform === 'capitalize' ? transform : 'none',
    tabularDigits: bool(s.tabularDigits, d.tabularDigits),
    shadow: bool(s.shadow, d.shadow),
    shadowColor: str(s.shadowColor, d.shadowColor),
    shadowBlur: num(s.shadowBlur, d.shadowBlur, 0, 200),
    shadowOffsetX: num(s.shadowOffsetX, d.shadowOffsetX, -200, 200),
    shadowOffsetY: num(s.shadowOffsetY, d.shadowOffsetY, -200, 200),
  };
}

/** Swatches offered next to every colour picker of the graphics sources. */
export const COLOUR_PRESETS = [
  '#ffffff',
  '#000000',
  '#ef4444',
  '#f97316',
  '#facc15',
  '#22c55e',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#00000080',
  '#00000000',
];

/**
 * True when `value` is a CSS colour the canvas will accept. Without CSS.supports
 * (unit tests in Node) everything passes, so only the browser rejects values.
 */
export function isValidColour(value: string): boolean {
  if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') return true;
  return CSS.supports('color', value);
}

/** The first invalid colour among `fields`, as a status message, or null. Empty strings mean "none". */
export function colourProblem(fields: Record<string, string>): string | null {
  for (const [label, value] of Object.entries(fields)) {
    if (value !== '' && !isValidColour(value)) return `${label}: "${value}" is not a valid colour`;
  }
  return null;
}

export function textStyleColourProblem(style: TextStyle): string | null {
  return colourProblem({
    Colour: style.color,
    Background: style.backgroundColor,
    'Outline colour': style.outlineWidth > 0 ? style.outlineColor : '',
    'Shadow colour': style.shadow ? style.shadowColor : '',
  });
}

export function textStyleProperties(): PropertyDescriptor[] {
  return [
    { key: 'fontFamily', label: 'Font', type: 'font' },
    {
      key: 'fontAssetId',
      label: 'Font file',
      type: 'asset',
      accept: '.ttf,.otf,.woff,.woff2,font/*',
      description: 'A .ttf, .otf, .woff or .woff2 file stored with the project. Overrides the font name, which stays as the fallback.',
    },
    { key: 'fontSize', label: 'Size', type: 'number', min: 1, max: 1000, step: 1, unit: 'px' },
    {
      key: 'fontWeight',
      label: 'Weight',
      type: 'select',
      options: [100, 200, 300, 400, 500, 600, 700, 800, 900].map((w) => ({ value: w, label: String(w) })),
    },
    { key: 'italic', label: 'Italic', type: 'boolean' },
    {
      key: 'textTransform',
      label: 'Case',
      type: 'select',
      options: [
        { value: 'none', label: 'As typed' },
        { value: 'uppercase', label: 'UPPERCASE' },
        { value: 'lowercase', label: 'lowercase' },
        { value: 'capitalize', label: 'Capitalise Each Word' },
      ],
    },
    { key: 'color', label: 'Colour', type: 'color', alpha: true, presets: COLOUR_PRESETS },
    { key: 'backgroundColor', label: 'Background', type: 'color', alpha: true, presets: COLOUR_PRESETS },
    { key: 'outlineWidth', label: 'Outline width', type: 'number', min: 0, max: 100, step: 1, unit: 'px' },
    { key: 'outlineColor', label: 'Outline colour', type: 'color', alpha: true, presets: COLOUR_PRESETS, visibleWhen: (s) => num(s.outlineWidth, 0) > 0 },
    {
      key: 'align',
      label: 'Alignment',
      type: 'select',
      options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Centre' },
        { value: 'right', label: 'Right' },
      ],
    },
    { key: 'lineHeight', label: 'Line height', type: 'number', min: 0.5, max: 5, step: 0.05 },
    { key: 'letterSpacing', label: 'Letter spacing', type: 'number', min: -100, max: 1000, step: 0.5, unit: 'px' },
    { key: 'wrapWidth', label: 'Wrap width', type: 'number', min: 0, max: 16384, step: 1, unit: 'px', description: '0 disables wrapping (a fixed box width still wraps).' },
    { key: 'boxWidth', label: 'Box width', type: 'number', min: 0, max: 16384, step: 1, unit: 'px', description: '0 fits the text.' },
    { key: 'boxHeight', label: 'Box height', type: 'number', min: 0, max: 16384, step: 1, unit: 'px', description: '0 fits the text. Text beyond a fixed box is clipped.' },
    {
      key: 'valign',
      label: 'Vertical alignment',
      type: 'select',
      options: [
        { value: 'top', label: 'Top' },
        { value: 'middle', label: 'Middle' },
        { value: 'bottom', label: 'Bottom' },
      ],
      visibleWhen: (s) => num(s.boxHeight, 0) > 0,
    },
    { key: 'padding', label: 'Padding', type: 'number', min: 0, max: 1000, step: 1, unit: 'px' },
    { key: 'shadow', label: 'Drop shadow', type: 'boolean' },
    { key: 'shadowColor', label: 'Shadow colour', type: 'color', alpha: true, presets: COLOUR_PRESETS, visibleWhen: (s) => s.shadow === true },
    { key: 'shadowBlur', label: 'Shadow blur', type: 'number', min: 0, max: 200, step: 1, unit: 'px', visibleWhen: (s) => s.shadow === true },
    { key: 'shadowOffsetX', label: 'Shadow offset X', type: 'number', min: -200, max: 200, step: 1, unit: 'px', visibleWhen: (s) => s.shadow === true },
    { key: 'shadowOffsetY', label: 'Shadow offset Y', type: 'number', min: -200, max: 200, step: 1, unit: 'px', visibleWhen: (s) => s.shadow === true },
    { key: 'warning', label: 'Problems', type: 'info', liveKey: 'warning' },
  ];
}

const GENERIC_FAMILY = /^(serif|sans-serif|monospace|cursive|fantasy|system-ui|ui-serif|ui-sans-serif|ui-monospace|ui-rounded|emoji|math|fangsong)$/i;

export function isGenericFamily(family: string): boolean {
  return GENERIC_FAMILY.test(family.trim());
}

/** The family names of a CSS font-family list, unquoted. */
export function familyList(fontFamily: string): string[] {
  return fontFamily
    .split(',')
    .map((f) => f.trim().replace(/^["'](.*)["']$/, '$1'))
    .filter(Boolean);
}

export function fontString(style: TextStyle): string {
  const family = style.fontFamily
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean)
    .map((f) => (/^["'].*["']$/.test(f) || isGenericFamily(f) || !/[\s\d]/.test(f) ? f : `"${f}"`))
    .join(', ');
  return `${style.italic ? 'italic ' : ''}${style.fontWeight} ${style.fontSize}px ${family}`;
}

export type MeasureFn = (text: string) => number;

function fitGraphemes(word: string, maxWidth: number, measure: MeasureFn, lines: string[], line: string): string {
  for (const ch of graphemes(word)) {
    if (line !== '' && measure(line + ch) > maxWidth) {
      lines.push(line);
      line = ch;
    } else {
      line += ch;
    }
  }
  return line;
}

/**
 * Greedy wrap at Unicode break opportunities. Explicit newlines always break;
 * a word wider than the limit is split between graphemes, never inside an
 * emoji sequence. `maxWidth <= 0` disables wrapping.
 */
export function wrapText(text: string, maxWidth: number, measure: MeasureFn): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split(/\r\n|\r|\n/)) {
    if (maxWidth <= 0 || paragraph === '' || measure(paragraph) <= maxWidth) {
      lines.push(paragraph);
      continue;
    }
    let line = '';
    for (const piece of breakPieces(paragraph)) {
      const candidate = line + piece;
      if (measure(candidate.trimEnd()) <= maxWidth) {
        line = candidate;
        continue;
      }
      if (line.trim() !== '') lines.push(line.trimEnd());
      line = '';
      const word = piece.trimEnd();
      if (measure(word) <= maxWidth) {
        line = piece;
        continue;
      }
      line = fitGraphemes(word, maxWidth, measure, lines, '');
      if (piece.length > word.length) line += piece.slice(word.length);
    }
    lines.push(line.trimEnd());
  }
  return lines;
}

export interface TextLayout {
  lines: string[];
  contentWidth: number;
  lineHeightPx: number;
  /** Space around the text for padding, outline and shadow. */
  margin: number;
  width: number;
  height: number;
  /** Top of the first line's box. */
  top: number;
}

export function layoutText(text: string, style: TextStyle, measure: MeasureFn): TextLayout {
  const shadowReach = style.shadow ? style.shadowBlur + Math.max(Math.abs(style.shadowOffsetX), Math.abs(style.shadowOffsetY)) : 0;
  const margin = Math.ceil(style.padding + style.outlineWidth + shadowReach);
  const wrapAt = style.wrapWidth > 0 ? style.wrapWidth : style.boxWidth > 0 ? Math.max(1, style.boxWidth - 2 * margin) : 0;
  const lines = wrapText(text, wrapAt, measure);
  const contentWidth = Math.max(0, ...lines.map(measure));
  const lineHeightPx = Math.max(1, Math.round(style.fontSize * style.lineHeight));
  const textHeight = lines.length * lineHeightPx;
  const width = style.boxWidth > 0 ? Math.round(style.boxWidth) : Math.max(1, Math.ceil(contentWidth) + 2 * margin);
  const height = style.boxHeight > 0 ? Math.round(style.boxHeight) : Math.max(1, textHeight + 2 * margin);
  const top =
    style.boxHeight > 0 && style.valign === 'middle'
      ? (height - textHeight) / 2
      : style.boxHeight > 0 && style.valign === 'bottom'
        ? height - margin - textHeight
        : margin;
  return { lines, contentWidth, lineHeightPx, margin, width, height, top };
}

/**
 * Canvas size for a raster of `width × height` layout pixels at `ratio`,
 * shrunk to fit MAX_RASTER_EDGE. `ratio` in the result is what was achieved;
 * below 1 the picture is softer than its layout size (huge text).
 */
export function rasterFit(width: number, height: number, ratio: number): { canvasWidth: number; canvasHeight: number; ratio: number } {
  const wanted = Math.max(0.01, ratio);
  const fit = Math.min(wanted, MAX_RASTER_EDGE / width, MAX_RASTER_EDGE / height);
  return {
    canvasWidth: Math.max(1, Math.min(MAX_RASTER_EDGE, Math.round(width * fit))),
    canvasHeight: Math.max(1, Math.min(MAX_RASTER_EDGE, Math.round(height * fit))),
    ratio: fit,
  };
}

type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

const DIGIT = /\d/u;

/** Measures with every ASCII digit as wide as the widest one when `tabular`. */
function measurer(ctx: Ctx2D, tabular: boolean): { measure: MeasureFn; digitWidth: number } {
  const plain: MeasureFn = (t) => ctx.measureText(t).width;
  if (!tabular) return { measure: plain, digitWidth: 0 };
  const digitWidth = Math.max(...'0123456789'.split('').map(plain));
  const measure: MeasureFn = (t) => {
    let w = 0;
    for (const run of t.match(/\d|\D+/gu) ?? []) w += DIGIT.test(run) ? digitWidth : plain(run);
    return w;
  };
  return { measure, digitWidth };
}

function setLetterSpacing(ctx: Ctx2D, px: number): void {
  const c = ctx as Ctx2D & { letterSpacing?: string };
  if ('letterSpacing' in c) c.letterSpacing = `${px}px`;
}

export interface TextRaster {
  /** Layout (natural) size; the canvas is this times `pixelRatio`, rounded. */
  width: number;
  height: number;
  pixelRatio: number;
}

/**
 * Sizes `canvas` around `text` and draws it at `ratio` canvas pixels per
 * layout pixel (raster scale), within MAX_RASTER_EDGE. Returns the layout size.
 */
export function drawText(canvas: HTMLCanvasElement | OffscreenCanvas, rawText: string, style: TextStyle, ratio = 1): TextRaster {
  const ctx = canvas.getContext('2d') as Ctx2D | null;
  if (!ctx) throw new Error('2D canvas is unavailable');
  const text = transformText(rawText, style.textTransform);
  const font = fontString(style);
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.font = font;
  setLetterSpacing(ctx, style.letterSpacing);
  const { measure, digitWidth } = measurer(ctx, style.tabularDigits);
  const layout = layoutText(text, style, measure);
  const fit = rasterFit(layout.width, layout.height, ratio);
  // Resizing resets the 2D state, which is why the font is set again below.
  if (canvas.width !== fit.canvasWidth) canvas.width = fit.canvasWidth;
  if (canvas.height !== fit.canvasHeight) canvas.height = fit.canvasHeight;
  const sx = fit.canvasWidth / layout.width;
  const sy = fit.canvasHeight / layout.height;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.clearRect(0, 0, fit.canvasWidth, fit.canvasHeight);
  // Glyphs are rasterised under the transform, at the canvas resolution, not scaled afterwards.
  ctx.setTransform(sx, 0, 0, sy, 0, 0);
  if (style.backgroundColor) {
    ctx.fillStyle = style.backgroundColor;
    ctx.fillRect(0, 0, layout.width, layout.height);
  }
  ctx.font = font;
  setLetterSpacing(ctx, style.letterSpacing);
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.miterLimit = 2;
  if (style.shadow) {
    // Shadow geometry ignores the transform, so it is scaled by hand.
    ctx.shadowColor = style.shadowColor;
    ctx.shadowBlur = style.shadowBlur * sx;
    ctx.shadowOffsetX = style.shadowOffsetX * sx;
    ctx.shadowOffsetY = style.shadowOffsetY * sy;
  }
  ctx.lineWidth = style.outlineWidth * 2;
  ctx.strokeStyle = style.outlineColor;
  ctx.fillStyle = style.color;
  const paint = (s: string, x: number, y: number) => {
    if (style.outlineWidth > 0) ctx.strokeText(s, x, y);
    ctx.fillText(s, x, y);
  };
  layout.lines.forEach((line, i) => {
    const y = layout.top + i * layout.lineHeightPx + layout.lineHeightPx / 2;
    if (!style.tabularDigits) {
      ctx.textAlign = style.align;
      const x = style.align === 'left' ? layout.margin : style.align === 'right' ? layout.width - layout.margin : layout.width / 2;
      paint(line, x, y);
      return;
    }
    const lineWidth = measure(line);
    let x = style.align === 'left' ? layout.margin : style.align === 'right' ? layout.width - layout.margin - lineWidth : (layout.width - lineWidth) / 2;
    for (const run of line.match(/\d|\D+/gu) ?? []) {
      if (DIGIT.test(run)) {
        ctx.textAlign = 'center';
        paint(run, x + digitWidth / 2, y);
        x += digitWidth;
      } else {
        ctx.textAlign = 'left';
        paint(run, x, y);
        x += ctx.measureText(run).width;
      }
    }
  });
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  return { width: layout.width, height: layout.height, pixelRatio: fit.ratio };
}

/**
 * Starts loading the font (web fonts and local fonts alike) and resolves true
 * if it was not yet available, meaning text drawn earlier used a fallback and
 * should be redrawn. Never rejects.
 */
export async function ensureFont(style: TextStyle, text: string): Promise<boolean> {
  if (typeof document === 'undefined' || !document.fonts) return false;
  const font = fontString(style);
  const sample = text.slice(0, 64) || ' ';
  try {
    if (document.fonts.check(font, sample)) return false;
    await document.fonts.load(font, sample);
    return true;
  } catch {
    return false;
  }
}
